import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { apiErrorMessage } from "@/lib/apiError";
import { Bot, Check, X, Edit, Plus, Trash2, Zap } from "lucide-react";

const ALL_RISK_FLAGS = [
  "escalation_threat", "legal_language", "angry_client", "payment_dispute",
  "missed_deadline", "scope_creep", "ghosting", "competitor_mention", "contract_terms",
];
const ALL_SENTIMENTS = ["angry", "frustrated", "neutral", "positive"];
const ALL_LEVELS = ["high", "normal"];
const ALL_ROLES = [
  ["owner", "Owners"],
  ["admin", "Admins"],
  ["project_manager", "Project managers (of linked project)"],
];
const ALL_CHANNELS = [
  ["in_app", "In-app notification"],
  ["ws", "Real-time WebSocket toast"],
  ["gmail", "Escalation email via admin's Gmail"],
];

export default function AutoResponse() {
  const { activeWorkspaceId } = useAuth();
  const [tab, setTab] = useState("rules"); // rules | reviews | history
  return (
    <div className="px-4 sm:px-6 md:px-10 py-8 max-w-6xl space-y-4" data-testid="auto-response-page">
      <div className="flex items-center gap-2">
        <Bot className="w-6 h-6 text-primary" />
        <h1 className="text-2xl font-bold">Auto-Response Rules</h1>
      </div>
      <p className="text-sm text-muted-foreground max-w-3xl">
        Configure rules that match incoming shared-inbox threads (by sentiment, risk flags, silence, message count) and
        either queue Claude&apos;s drafted response for owner/admin/PM sign-off or safe-send it directly. Every fire is logged.
      </p>
      <div className="flex items-center gap-1 border border-border p-1 w-fit" data-testid="ar-tabs">
        {[
          ["rules", "Rules"],
          ["reviews", "Pending reviews"],
          ["history", "History"],
        ].map(([v, lbl]) => (
          <button key={v} onClick={() => setTab(v)}
            className={`px-3 py-1 text-xs uppercase tracking-widest ${tab === v ? "bg-foreground text-background" : "text-muted-foreground hover:text-foreground"}`}
            data-testid={`ar-tab-${v}`}>{lbl}</button>
        ))}
      </div>
      {tab === "rules" && <RulesTab wsId={activeWorkspaceId} />}
      {tab === "reviews" && <ReviewsTab wsId={activeWorkspaceId} />}
      {tab === "history" && <HistoryTab wsId={activeWorkspaceId} />}
    </div>
  );
}

function RulesTab({ wsId }) {
  const [rules, setRules] = useState([]);
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    setBusy(true);
    try {
      const { data } = await api.get(`/workspaces/${wsId}/auto-response/rules`);
      setRules(data.rules || []);
    } catch (e) { toast.error(apiErrorMessage(e) || "Failed to load rules"); }
    finally { setBusy(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [wsId]);

  const toggleEnabled = async (rule) => {
    try {
      await api.patch(`/workspaces/${wsId}/auto-response/rules/${rule.id}`, { enabled: !rule.enabled });
      toast.success(`Rule ${!rule.enabled ? "enabled" : "disabled"}`);
      load();
    } catch (e) { toast.error(apiErrorMessage(e) || "Toggle failed"); }
  };
  const remove = async (rule) => {
    if (!window.confirm(`Delete rule "${rule.name}"?`)) return;
    try {
      await api.delete(`/workspaces/${wsId}/auto-response/rules/${rule.id}`);
      toast.success("Deleted");
      load();
    } catch (e) { toast.error(apiErrorMessage(e) || "Delete failed"); }
  };

  return (
    <div className="space-y-3">
      <div className="flex justify-end">
        <Button className="rounded-none" onClick={() => setEditing({})} data-testid="ar-new-rule">
          <Plus className="w-4 h-4 mr-1" /> New rule
        </Button>
      </div>
      {busy && rules.length === 0 ? (
        <div className="text-sm text-muted-foreground">Loading…</div>
      ) : rules.length === 0 ? (
        <div className="border border-dashed border-border p-10 text-center text-sm text-muted-foreground">
          <Bot className="w-8 h-8 mx-auto mb-2 opacity-40" />
          No rules yet. Add one to auto-triage HIGH-risk client replies.
        </div>
      ) : (
        <div className="space-y-2" data-testid="ar-rules-list">
          {rules.map((r) => (
            <div key={r.id} className={`border border-border p-4 ${r.enabled ? "" : "opacity-60"}`} data-testid={`ar-rule-${r.id}`}>
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold">{r.name}</span>
                    <span className="text-[10px] uppercase tracking-widest border border-border px-1.5 py-0.5">
                      Priority {r.priority}
                    </span>
                    {r.action?.require_review ? (
                      <span className="text-[10px] uppercase tracking-widest bg-amber-50 text-amber-800 border border-amber-300 px-1.5 py-0.5">
                        Review required
                      </span>
                    ) : (
                      <span className="text-[10px] uppercase tracking-widest bg-emerald-50 text-emerald-800 border border-emerald-300 px-1.5 py-0.5">
                        <Zap className="w-3 h-3 inline mr-0.5" /> Safe-send
                      </span>
                    )}
                    <span className="text-[11px] text-muted-foreground">
                      Fired {r.fire_count || 0} time{r.fire_count === 1 ? "" : "s"}
                    </span>
                  </div>
                  {r.description && <p className="text-xs text-muted-foreground mt-1">{r.description}</p>}
                  <div className="flex flex-wrap gap-1 mt-2">
                    {(r.trigger?.risk_flags || []).map((f) => (
                      <span key={f} className="text-[10px] uppercase tracking-widest border border-red-300 bg-red-50 text-red-700 px-1.5 py-0.5">
                        {f.replace(/_/g, " ")}
                      </span>
                    ))}
                    {(r.trigger?.sentiments || []).map((s) => (
                      <span key={s} className="text-[10px] uppercase tracking-widest border border-amber-300 bg-amber-50 text-amber-700 px-1.5 py-0.5">
                        {s}
                      </span>
                    ))}
                    {r.trigger?.silence_hours && (
                      <span className="text-[10px] uppercase tracking-widest border border-blue-300 bg-blue-50 text-blue-700 px-1.5 py-0.5">
                        silent ≥ {r.trigger.silence_hours}h
                      </span>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Switch checked={r.enabled} onCheckedChange={() => toggleEnabled(r)} data-testid={`ar-toggle-${r.id}`} />
                  <Button size="sm" variant="ghost" className="rounded-none h-8" onClick={() => setEditing(r)} data-testid={`ar-edit-${r.id}`}>
                    <Edit className="w-3.5 h-3.5" />
                  </Button>
                  <Button size="sm" variant="ghost" className="rounded-none h-8 text-red-600" onClick={() => remove(r)} data-testid={`ar-delete-${r.id}`}>
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
      {editing && (
        <RuleEditor wsId={wsId} rule={editing.id ? editing : null}
                     onClose={() => setEditing(null)}
                     onSaved={() => { setEditing(null); load(); }} />
      )}
    </div>
  );
}

function RuleEditor({ wsId, rule, onClose, onSaved }) {
  const isEdit = !!rule?.id;
  const [name, setName] = useState(rule?.name || "");
  const [description, setDescription] = useState(rule?.description || "");
  const [priority, setPriority] = useState(rule?.priority ?? 100);
  const [enabled, setEnabled] = useState(rule?.enabled ?? true);
  const [sentiments, setSentiments] = useState(rule?.trigger?.sentiments || []);
  const [riskFlags, setRiskFlags] = useState(rule?.trigger?.risk_flags || []);
  const [riskLevels, setRiskLevels] = useState(rule?.trigger?.risk_levels || ["high"]);
  const [silenceHours, setSilenceHours] = useState(rule?.trigger?.silence_hours || "");
  const [minMessages, setMinMessages] = useState(rule?.trigger?.min_message_count || "");
  const [requireReview, setRequireReview] = useState(rule?.action?.require_review ?? true);
  const [tpl, setTpl] = useState(rule?.action?.override_html_template || "");
  const [cooldownH, setCooldownH] = useState(rule?.action?.cooldown_hours ?? 24);
  const [roles, setRoles] = useState(rule?.reviewers?.roles || ["owner", "admin"]);
  const [channels, setChannels] = useState(rule?.notify_channels || ["in_app", "ws"]);
  const [busy, setBusy] = useState(false);

  const toggle = (list, setList, v) => {
    setList(list.includes(v) ? list.filter((x) => x !== v) : [...list, v]);
  };

  const save = async () => {
    if (name.trim().length < 3) { toast.error("Rule name must be at least 3 characters"); return; }
    const payload = {
      name: name.trim(), description: description.trim() || undefined,
      enabled, priority: Number(priority) || 100,
      trigger: {
        sentiments, risk_flags: riskFlags, risk_levels: riskLevels,
        silence_hours: silenceHours ? Number(silenceHours) : null,
        min_message_count: minMessages ? Number(minMessages) : null,
      },
      action: {
        require_review: requireReview,
        override_html_template: tpl.trim() || null,
        cooldown_hours: Number(cooldownH) || 24,
      },
      reviewers: { roles },
      notify_channels: channels,
    };
    setBusy(true);
    try {
      if (isEdit) {
        await api.patch(`/workspaces/${wsId}/auto-response/rules/${rule.id}`, payload);
        toast.success("Rule updated");
      } else {
        await api.post(`/workspaces/${wsId}/auto-response/rules`, payload);
        toast.success("Rule created");
      }
      onSaved && onSaved();
    } catch (e) { toast.error(apiErrorMessage(e) || "Save failed"); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="ar-rule-editor"
                     description="Configure a rule to auto-triage inbound client replies on the shared workspace mailbox.">
        <DialogHeader><DialogTitle>{isEdit ? "Edit rule" : "New rule"}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div className="grid sm:grid-cols-3 gap-3">
            <div className="sm:col-span-2">
              <Label className="label-eyebrow mb-1 inline-block">Rule name *</Label>
              <Input value={name} maxLength={80} onChange={(e) => setName(e.target.value)}
                     placeholder="e.g. Auto-ack refund threats" className="h-10 rounded-none" data-testid="ar-name" />
            </div>
            <div>
              <Label className="label-eyebrow mb-1 inline-block">Priority (lower = first)</Label>
              <Input type="number" value={priority} onChange={(e) => setPriority(e.target.value)}
                     min={0} max={1000} className="h-10 rounded-none" data-testid="ar-priority" />
            </div>
          </div>
          <div>
            <Label className="label-eyebrow mb-1 inline-block">Description</Label>
            <Textarea rows={2} value={description} maxLength={400} onChange={(e) => setDescription(e.target.value)}
                      placeholder="What does this rule do?" className="rounded-none text-sm" data-testid="ar-description" />
          </div>

          <div className="border-t border-border pt-3 space-y-2">
            <div className="label-eyebrow">Trigger — fire when ALL match</div>
            <ChipGroup label="Risk flags (any)" options={ALL_RISK_FLAGS} selected={riskFlags} onToggle={(v) => toggle(riskFlags, setRiskFlags, v)} testid="ar-risk-flags" />
            <ChipGroup label="Sentiments (any)" options={ALL_SENTIMENTS} selected={sentiments} onToggle={(v) => toggle(sentiments, setSentiments, v)} testid="ar-sentiments" />
            <ChipGroup label="Risk level" options={ALL_LEVELS} selected={riskLevels} onToggle={(v) => toggle(riskLevels, setRiskLevels, v)} testid="ar-risk-levels" />
            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <Label className="label-eyebrow mb-1 inline-block">Only after silence ≥ (hours)</Label>
                <Input type="number" value={silenceHours} onChange={(e) => setSilenceHours(e.target.value)}
                       placeholder="e.g. 48" className="h-10 rounded-none" data-testid="ar-silence-h" />
              </div>
              <div>
                <Label className="label-eyebrow mb-1 inline-block">Only when thread has ≥ N messages</Label>
                <Input type="number" value={minMessages} onChange={(e) => setMinMessages(e.target.value)}
                       placeholder="e.g. 2" className="h-10 rounded-none" data-testid="ar-min-msg" />
              </div>
            </div>
          </div>

          <div className="border-t border-border pt-3 space-y-2">
            <div className="label-eyebrow">Action</div>
            <label className="flex items-center gap-2 text-sm">
              <Switch checked={requireReview} onCheckedChange={setRequireReview} data-testid="ar-require-review" />
              Require human review before sending (recommended for HIGH-risk)
            </label>
            <div>
              <Label className="label-eyebrow mb-1 inline-block">Response template (HTML, optional)</Label>
              <Textarea rows={5} value={tpl} onChange={(e) => setTpl(e.target.value)}
                        placeholder={"Leave blank to use Claude's ai_suggested_reply.\nSupported vars: {{summary}} {{next_action}} {{sentiment}} {{customer_name}} {{subject}}"}
                        className="rounded-none text-xs font-mono" data-testid="ar-template" />
            </div>
            <div>
              <Label className="label-eyebrow mb-1 inline-block">Cooldown per thread (hours)</Label>
              <Input type="number" value={cooldownH} onChange={(e) => setCooldownH(e.target.value)}
                     min={0} className="h-10 rounded-none max-w-xs" data-testid="ar-cooldown" />
            </div>
          </div>

          <div className="border-t border-border pt-3 space-y-2">
            <div className="label-eyebrow">Reviewers</div>
            <ChipGroup label="Which roles get the review request" options={ALL_ROLES} selected={roles} onToggle={(v) => toggle(roles, setRoles, v)} testid="ar-roles" />
            <ChipGroup label="Notification channels" options={ALL_CHANNELS} selected={channels} onToggle={(v) => toggle(channels, setChannels, v)} testid="ar-channels" />
          </div>

          <div className="flex items-center gap-2">
            <Switch checked={enabled} onCheckedChange={setEnabled} data-testid="ar-enabled" />
            <span className="text-sm">Enabled</span>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" className="rounded-none" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button className="rounded-none" onClick={save} disabled={busy || name.trim().length < 3} data-testid="ar-save-rule">
            <Check className="w-4 h-4 mr-1" /> {busy ? "Saving…" : "Save rule"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ChipGroup({ label, options, selected, onToggle, testid }) {
  return (
    <div>
      <Label className="label-eyebrow mb-1 inline-block">{label}</Label>
      <div className="flex flex-wrap gap-2" data-testid={testid}>
        {options.map((o) => {
          const [v, lbl] = Array.isArray(o) ? o : [o, o.replace(/_/g, " ")];
          const on = selected.includes(v);
          return (
            <button key={v} type="button" onClick={() => onToggle(v)}
                    className={`px-2 py-1 text-xs uppercase tracking-widest border ${on ? "border-foreground bg-foreground text-background" : "border-border hover:border-foreground/60"}`}
                    data-testid={`${testid}-${v}`}>
              {lbl}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function ReviewsTab({ wsId }) {
  const [reviews, setReviews] = useState([]);
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    setBusy(true);
    try {
      const { data } = await api.get(`/workspaces/${wsId}/auto-response/reviews`);
      setReviews(data.reviews || []);
    } catch (e) { toast.error(apiErrorMessage(e) || "Failed to load reviews"); }
    finally { setBusy(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [wsId]);

  const approve = async (rev) => {
    if (!window.confirm(`Send this draft as-is to ${rev.customer_email}?`)) return;
    try {
      await api.post(`/workspaces/${wsId}/auto-response/reviews/${rev.id}/approve`);
      toast.success("Sent");
      load();
    } catch (e) { toast.error(apiErrorMessage(e) || "Send failed"); }
  };
  const reject = async (rev) => {
    if (!window.confirm("Reject and don't send?")) return;
    try {
      await api.post(`/workspaces/${wsId}/auto-response/reviews/${rev.id}/reject`);
      toast.success("Rejected");
      load();
    } catch (e) { toast.error(apiErrorMessage(e) || "Reject failed"); }
  };

  return (
    <div className="space-y-3">
      {busy && reviews.length === 0 ? (
        <div className="text-sm text-muted-foreground">Loading…</div>
      ) : reviews.length === 0 ? (
        <div className="border border-dashed border-emerald-300 bg-emerald-50 p-8 text-center text-sm text-emerald-800">
          Nothing to review right now. Rules are watching.
        </div>
      ) : (
        reviews.map((r) => (
          <div key={r.id} className="border border-border p-4" data-testid={`ar-review-${r.id}`}>
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-semibold text-sm">{r.subject || "(no subject)"}</span>
                  <span className="text-[10px] uppercase tracking-widest border border-primary bg-primary/10 text-primary px-1.5 py-0.5">
                    Rule: {r.rule_name}
                  </span>
                  <span className="text-[10px] uppercase tracking-widest border border-red-300 bg-red-50 text-red-700 px-1.5 py-0.5">
                    {r.sentiment || "high-risk"}
                  </span>
                </div>
                <div className="text-xs text-muted-foreground mt-1">To: {r.customer_email}</div>
                {r.ai_summary && <p className="text-sm mt-1 italic">&ldquo;{r.ai_summary}&rdquo;</p>}
                <div className="text-xs bg-muted mt-2 p-3 max-h-40 overflow-y-auto"
                     dangerouslySetInnerHTML={{ __html: r.draft_html }} />
              </div>
              <div className="flex flex-col gap-2">
                <Button size="sm" className="rounded-none h-8" onClick={() => approve(r)} data-testid={`ar-approve-${r.id}`}>
                  <Check className="w-3.5 h-3.5 mr-1" /> Approve & send
                </Button>
                <Button size="sm" variant="outline" className="rounded-none h-8" onClick={() => setEditing(r)} data-testid={`ar-edit-review-${r.id}`}>
                  <Edit className="w-3.5 h-3.5 mr-1" /> Edit & send
                </Button>
                <Button size="sm" variant="ghost" className="rounded-none h-8 text-red-600" onClick={() => reject(r)} data-testid={`ar-reject-${r.id}`}>
                  <X className="w-3.5 h-3.5 mr-1" /> Reject
                </Button>
              </div>
            </div>
          </div>
        ))
      )}
      {editing && <EditReviewDialog wsId={wsId} review={editing} onClose={() => setEditing(null)} onSent={() => { setEditing(null); load(); }} />}
    </div>
  );
}

function EditReviewDialog({ wsId, review, onClose, onSent }) {
  const [html, setHtml] = useState(review.draft_html || "");
  const [busy, setBusy] = useState(false);

  const send = async () => {
    setBusy(true);
    try {
      await api.post(`/workspaces/${wsId}/auto-response/reviews/${review.id}/edit-and-send`,
        { override_html: html });
      toast.success("Sent");
      onSent && onSent();
    } catch (e) { toast.error(apiErrorMessage(e) || "Send failed"); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="ar-edit-review-dialog"
                     description="Edit the auto-response draft before sending. Sent via shared workspace mailbox.">
        <DialogHeader><DialogTitle>Edit &amp; send — {review.subject}</DialogTitle></DialogHeader>
        <Textarea rows={16} value={html} onChange={(e) => setHtml(e.target.value)}
                  className="font-mono text-xs rounded-none" data-testid="ar-edit-review-body" />
        <DialogFooter>
          <Button variant="ghost" className="rounded-none" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button className="rounded-none" onClick={send} disabled={busy || !html.trim()} data-testid="ar-edit-review-send">
            {busy ? "Sending…" : "Send edited draft"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function HistoryTab({ wsId }) {
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);
  const load = async () => {
    setBusy(true);
    try {
      const { data } = await api.get(`/workspaces/${wsId}/auto-response/history`);
      setRows(data.history || []);
    } catch (e) { toast.error(apiErrorMessage(e) || "Failed to load history"); }
    finally { setBusy(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [wsId]);
  if (busy) return <div className="text-sm text-muted-foreground">Loading…</div>;
  if (rows.length === 0) return <div className="border border-dashed border-border p-8 text-center text-sm text-muted-foreground">No rules have fired yet.</div>;
  return (
    <div className="border border-border">
      <table className="w-full text-sm">
        <thead className="bg-muted/40 text-[10px] uppercase tracking-widest">
          <tr>
            <th className="text-left p-2">When</th>
            <th className="text-left p-2">Rule</th>
            <th className="text-left p-2">Action</th>
            <th className="text-left p-2">Thread</th>
            <th className="text-left p-2">Actor</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.id} className="border-t border-border" data-testid={`ar-hist-${r.id}`}>
              <td className="p-2 text-xs text-muted-foreground">{new Date(r.created_at).toLocaleString()}</td>
              <td className="p-2 text-xs font-mono">{r.rule_id?.slice(0, 8)}</td>
              <td className="p-2 text-xs uppercase tracking-widest">
                <span className={
                  r.action === "auto_sent" ? "text-emerald-700" :
                  r.action?.startsWith("sent_via_") ? "text-primary" :
                  r.action === "rejected" ? "text-red-700" : "text-muted-foreground"
                }>{r.action?.replace(/_/g, " ")}</span>
              </td>
              <td className="p-2 text-xs font-mono">{r.thread_id?.slice(0, 8)}</td>
              <td className="p-2 text-xs">{r.actor_user_id?.slice(0, 8) || "—"}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
